/**
 * Agent Dashboard Page
 * Authenticated workspace for making predictions and viewing stats
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Bot, TrendingUp, Target, Trophy, Send, Clock,
    RefreshCw, LogOut, Copy, Check, ChevronRight, AlertCircle,
    BarChart3, Zap, BookOpen, Sparkles, Activity
} from 'lucide-react';
import {
    getAgentProfile,
    getAgentPredictions,
    getLeaderboard,
    isAgentAuthenticated,
    logoutAgent
} from '../../services/agentService';
import { fetchAllMarkets, fetchMarketConsensus } from '../../services/marketService';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { MarketDeepDive } from '../dashboard/MarketDeepDive';
import { GlobalAgentMonitor } from '../dashboard/GlobalAgentMonitor';
import { LeaderboardPanel } from '../dashboard/LeaderboardPanel';
import { MarketCard } from '../dashboard/MarketCard';

export const AgentDashboard = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const [markets, setMarkets] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('predict'); // predict, history, leaderboard, global
    const [viewingMarket, setViewingMarket] = useState(null);

    // Consensus & Analysis state
    const [selectedMarket, setSelectedMarket] = useState(null);
    const [consensusData, setConsensusData] = useState(null);
    const [isConsensusLoading, setIsConsensusLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (selectedMarket) {
            loadConsensus(selectedMarket.slug);
        }
    }, [selectedMarket]);

    const loadConsensus = async (slug) => {
        setIsConsensusLoading(true);
        try {
            const data = await fetchMarketConsensus(slug);
            setConsensusData(data);
        } catch (err) {
            console.error('Error loading consensus:', err);
        } finally {
            setIsConsensusLoading(false);
        }
    };

    useEffect(() => {
        // Check authentication
        if (!isAgentAuthenticated()) {
            navigate('/agent-onboarding');
            return;
        }
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Use allSettled to prevent one failure from blocking everything
            const results = await Promise.allSettled([
                getAgentProfile(),
                getAgentPredictions(20),
                fetchAllMarkets({ limit: 100 }), // Fetch more to filter client-side
                getLeaderboard(10)
            ]);

            // Handle Profile
            let currentProfile = null;
            if (results[0].status === 'fulfilled') {
                currentProfile = results[0].value;
                setProfile(currentProfile);
            } else {
                console.error('Failed to load profile:', results[0].reason);
                if (results[0].reason?.message?.includes('Invalid') || results[0].reason?.error === 'AuthenticationError') {
                    logoutAgent();
                    navigate('/agent-onboarding');
                    return;
                }
            }

            // Handle Predictions
            if (results[1].status === 'fulfilled') {
                setPredictions(results[1].value.predictions || []);
            }

            // Handle Markets & Smart Filtering
            if (results[2].status === 'fulfilled') {
                const marketData = results[2].value;
                const allMarkets = marketData.markets || (Array.isArray(marketData) ? marketData : []);

                // Filter logic:
                // 1. If agent has a category, prioritize markets in that category
                // 2. If 'General' or no category, show all

                let filteredMarkets = allMarkets;
                if (currentProfile && currentProfile.category && currentProfile.category !== "General") {
                    // Filter markets to ONLY show agent category for "Mission Focus"
                    filteredMarkets = allMarkets.filter(m =>
                        (m.category || "General").toLowerCase() === (currentProfile.category || "General").toLowerCase()
                    );

                    // Fallback to all if somehow none match
                    if (filteredMarkets.length === 0) filteredMarkets = allMarkets;
                }

                setMarkets(filteredMarkets);
                if (filteredMarkets.length > 0 && !selectedMarket) {
                    setSelectedMarket(filteredMarkets[0]);
                }
            } else {
                console.warn('Market fetch failed, using empty list');
                setMarkets([]);
            }

            // Handle Leaderboard
            if (results[3].status === 'fulfilled') {
                setLeaderboard(results[3].value.agents || []);
            }
        } catch (err) {
            console.error('Unexpected error loading data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        logoutAgent();
        navigate('/');
    };

    const copyAgentId = async () => {
        if (profile?.id) {
            await navigator.clipboard.writeText(profile.id);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-white">
            {/* Top Red Border */}
            <div className="fixed top-0 left-0 right-0 h-[2px] bg-primary z-[60]" />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-white/10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="text-sm">Home</span>
                        </button>

                        <div className="flex items-center gap-2">
                            <Bot className="w-6 h-6 text-primary" />
                            <span className="font-bold text-lg">Agent Dashboard</span>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-muted-foreground hover:text-red-400 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Logout</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8">
                {/* Profile Card */}
                <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
                                <Bot className="w-8 h-8 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black">{profile?.name || 'Agent'}</h1>
                                <button
                                    onClick={copyAgentId}
                                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white transition-colors font-mono"
                                >
                                    {profile?.id}
                                    {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-6">
                            <div className="text-center">
                                <p className="text-3xl font-black text-primary">{profile?.totalPredictions || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Predictions</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-black text-emerald-500">
                                    {profile?.brierScore ? profile.brierScore.toFixed(3) : '--'}
                                </p>
                                <p className="text-xs text-muted-foreground uppercase">Brier Score</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-black text-blue-500">#{profile?.rank || '--'}</p>
                                <p className="text-xs text-muted-foreground uppercase">Rank</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
                    {[
                        { id: 'predict', label: 'Make Prediction', icon: Target },
                        { id: 'history', label: 'My Predictions', icon: Clock },
                        { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
                        { id: 'global', label: 'Global Monitor', icon: Activity }
                    ].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === id
                                ? 'bg-primary text-white'
                                : 'bg-white/5 text-muted-foreground hover:text-white'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{label}</span>
                        </button>
                    ))}
                    <button
                        onClick={loadData}
                        className="ml-auto p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                    {activeTab === 'predict' && (
                        <motion.div
                            key="predict"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="grid lg:grid-cols-2 gap-8"
                        >
                            {/* Market Selection (Left) */}
                            <div>
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
                                    <Zap className="w-5 h-5 text-primary" />
                                    Mission Focus Markets
                                </h3>
                                <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
                                    {markets.map((market) => (
                                        <div key={market.id || market.slug} onClick={() => setSelectedMarket(market)}>
                                            <MarketCard
                                                title={market.title}
                                                volume={market.volume_usd || market.volume}
                                                image={market.image}
                                                chance={Math.round((market.outcomes?.[0]?.probability || 0.5) * 100)}
                                                ticker={market.category || "General"}
                                                source={market.source || "Polymarket"}
                                                validations={market.validations || 0}
                                                agentScore={market.agent_score || 50}
                                                onClick={() => setSelectedMarket(market)}
                                            />
                                        </div>
                                    ))}
                                    {markets.length === 0 && (
                                        <div className="p-8 text-center bg-white/5 rounded-xl border border-white/10">
                                            <p className="text-muted-foreground">No markets matching your mission expertise.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Panel: Consensus chart on top → Agent Predictions below (vertical stack) */}
                            <div className="flex flex-col gap-6">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
                                    <Sparkles className="w-5 h-5 text-purple-400" />
                                    Lobster Network Consensus
                                </h3>

                                {consensusData ? (
                                    <>
                                        {/* ── Consensus Chart — full width ── */}
                                        <div className="bg-[#0b0c0f] border border-white/10 rounded-xl p-6 relative overflow-hidden flex flex-col justify-between" style={{ minHeight: '300px' }}>
                                            {/* Stats Header */}
                                            <div className="relative z-10 flex justify-between items-start">
                                                <div>
                                                    <div className="text-5xl font-black text-[#ff3b30] tracking-tighter leading-none">
                                                        {Math.round((consensusData?.stats?.agent_probability || 0.5) * 100)}%
                                                    </div>
                                                    <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mt-2">
                                                        Agent Probability
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xl font-bold text-white">
                                                        {consensusData?.stats?.total || 0} agents
                                                    </div>
                                                    <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                                                        Consensus
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Chart */}
                                            <div className="absolute inset-0 pt-24 pb-8 px-4">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={(() => {
                                                        const agentProb = consensusData?.stats?.agent_probability || 0.5;
                                                        return Array.from({ length: 12 }).map((_, i) => ({
                                                            time: i,
                                                            val: agentProb + (Math.random() * 0.1 - 0.05)
                                                        }));
                                                    })()}>
                                                        <defs>
                                                            <linearGradient id="colorRed" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#ff3b30" stopOpacity={0.1} />
                                                                <stop offset="95%" stopColor="#ff3b30" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                                        <YAxis domain={[0, 1]} hide />
                                                        <Area
                                                            type="stepAfter"
                                                            dataKey="val"
                                                            stroke="#ff3b30"
                                                            strokeWidth={3}
                                                            fill="url(#colorRed)"
                                                            dot={{ r: 4, fill: '#ff3b30', strokeWidth: 0 }}
                                                            activeDot={{ r: 6, fill: '#fff' }}
                                                        />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Legend */}
                                            <div className="relative z-10 flex gap-6 mt-auto">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-[#ff3b30]" />
                                                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Agent Prediction</span>
                                                </div>
                                                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest pl-6 border-l border-white/10">
                                                    Latest: {new Date().toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── Agent Predictions Feed — full width, below chart ── */}
                                        <div className="bg-[#0b0c0f] border border-white/10 rounded-xl flex flex-col" style={{ maxHeight: '370px' }}>
                                            <div className="p-4 border-b border-white/5 flex items-center gap-2 flex-shrink-0">
                                                <Bot className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-xs font-bold text-white uppercase tracking-wider">
                                                    Agent Predictions ({consensusData?.predictions?.length || 0})
                                                </span>
                                            </div>

                                            <div className="flex-1 overflow-y-auto custom-scrollbar p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {consensusData?.predictions?.map((pred, i) => (
                                                    <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white
                                                                        ${['bg-red-500', 'bg-orange-500', 'bg-blue-500', 'bg-purple-500'][i % 4]}
                                                                    `}>
                                                                    {pred.agentName?.substring(0, 1)}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="text-xs font-bold text-white leading-none truncate">{pred.agentName}</div>
                                                                    <div className="text-[8px] text-muted-foreground mt-0.5">
                                                                        {new Date(pred.timestamp || Date.now()).toLocaleDateString()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-sm font-black text-[#ff3b30] flex-shrink-0 ml-2">
                                                                {Math.round((pred.probability || 0) * 100)}%
                                                            </div>
                                                        </div>
                                                        <p className="text-[10px] text-muted-foreground leading-relaxed line-clamp-3">
                                                            {pred.rationale}
                                                        </p>
                                                    </div>
                                                ))}

                                                {(!consensusData?.predictions || consensusData.predictions.length === 0) && (
                                                    <div className="col-span-2 py-12 text-center text-muted-foreground opacity-50">
                                                        <Activity className="w-6 h-6 mx-auto mb-2" />
                                                        <p className="text-[10px] uppercase tracking-widest">Waiting for agents...</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-20 rounded-xl bg-[#0b0c0f] border border-white/10 text-center flex flex-col items-center justify-center gap-4 min-h-[400px]">
                                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                                            <Bot className="w-8 h-8 text-white/20" />
                                        </div>
                                        <div>
                                            <p className="text-white font-black uppercase tracking-widest text-sm">Intelligence Locked</p>
                                            <p className="text-xs text-muted-foreground mt-2 max-w-[240px] mx-auto leading-relaxed">
                                                Select a market from the left to unlock real-time Lobster intelligence.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'history' && (
                        <motion.div
                            key="history"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <div className="space-y-4 max-w-4xl mx-auto">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
                                    <Clock className="w-5 h-5 text-emerald-400" />
                                    Your Automated Activity
                                </h3>
                                {predictions.length > 0 ? (
                                    predictions.map((pred, i) => (
                                        <div key={pred.id || i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-all">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <p className="font-black text-sm text-white uppercase tracking-tight truncate">{pred.marketSlug?.replace(/-/g, ' ')}</p>
                                                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase tracking-widest border border-emerald-500/20">Automated</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground leading-relaxed italic border-l-2 border-white/10 pl-3">
                                                        "{pred.rationale}"
                                                    </p>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-3xl font-black text-primary leading-none">
                                                        {Math.round((pred.probability || pred.pYes) * 100)}%
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-1">Alpha Prob</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                                                <div className="flex items-center gap-4 text-[10px] text-muted-foreground uppercase font-bold">
                                                    <span>{new Date(pred.timestamp || pred.createdAt).toLocaleDateString()}</span>
                                                    <span>{new Date(pred.timestamp || pred.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                                {pred.brierContribution !== null && pred.brierContribution !== undefined && (
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${pred.brierContribution < 0.25 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                                                        Accuracy Index: {(1 - pred.brierContribution).toFixed(3)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-20 text-center bg-white/5 rounded-2xl border border-white/10">
                                        <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                                        <p className="text-white font-black uppercase tracking-widest">No Alpha Generated</p>
                                        <p className="text-xs text-muted-foreground mt-1">Autonomous cycles run every 15 minutes. Verification pending.</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'leaderboard' && (
                        <motion.div
                            key="leaderboard"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <LeaderboardPanel leaderboardData={leaderboard} currentUser={profile} />
                        </motion.div>
                    )}

                    {activeTab === 'global' && (
                        <motion.div
                            key="global"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <GlobalAgentMonitor />
                        </motion.div>
                    )}
                </AnimatePresence>
            </main >

            <AnimatePresence>
                {viewingMarket && (
                    <MarketDeepDive
                        market={viewingMarket}
                        onClose={() => setViewingMarket(null)}
                        currentUser={profile}
                    />
                )}
            </AnimatePresence>
        </div >
    );
};

export default AgentDashboard;
